import React from 'react'
import { Box, Card, Typography, Stack, Chip, Link } from '@mui/material'

/**
 * Types locaux miroirs de SourceMetadata dans @data-agents/database.
 * Gardés inline pour éviter d'importer une dépendance lourde côté React build
 * (cf. Task 16 / ProposalSourceChip qui suit le même pattern).
 *
 * Note: l'API backend (`proposals-from-event-page.ts`) accepte un schéma laxiste
 * `z.record(z.string(), z.unknown())` — les turns envoyés par Miles ont des
 * formes variées (text / url / image / pdf, avec ou sans timestamp). On reflète
 * cette réalité ici en typant les champs comme optionnels et en parsant
 * défensivement avant rendu.
 */
interface ChatTurn {
  role?: 'user' | 'bot'
  timestamp?: string
  content?: unknown
  [key: string]: unknown
}

const TEXT_FIELD_FALLBACKS = ['content', 'text', 'message', 'url', 'imageUrl', 'pdfUrl'] as const

/**
 * URLs nues dans un tour de conversation. Les caractères de ponctuation qui
 * collent à la fin d'une phrase ne font pas partie de l'URL.
 */
const URL_PATTERN = /https?:\/\/[^\s<>]+/g
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/

/**
 * Découpe un texte en segments texte / lien cliquable.
 *
 * On ne rend jamais de HTML brut : chaque segment est un nœud React, donc une
 * URL forgée dans un message de contributeur ne peut pas injecter de markup.
 */
function renderTextWithLinks(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  const pattern = new RegExp(URL_PATTERN)
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    const raw = match[0]
    const url = raw.replace(TRAILING_PUNCTUATION, '')
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index))
    nodes.push(
      <Link key={`${match.index}-${url}`} href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </Link>,
    )
    const trailing = raw.slice(url.length)
    if (trailing) nodes.push(trailing)
    lastIndex = match.index + raw.length
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
  return nodes
}

function parseTurn(turn: ChatTurn): { date: Date | null; text: string | null; isBot: boolean } {
  let date: Date | null = null
  if (typeof turn.timestamp === 'string' && turn.timestamp) {
    const d = new Date(turn.timestamp)
    if (!Number.isNaN(d.getTime())) date = d
  }

  let text: string | null = null
  for (const key of TEXT_FIELD_FALLBACKS) {
    const value = turn[key]
    if (typeof value === 'string' && value.trim()) {
      text = value
      break
    }
  }

  return { date, text, isBot: turn.role === 'bot' }
}

interface ConversationProposer {
  userId?: string
  email?: string
  firstName?: string
  lastName?: string
  locale?: string
}

interface ConversationSourceMetadata {
  type: string
  extra?: {
    conversationHistory?: ChatTurn[]
    proposer?: ConversationProposer
    proposerNote?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface ConversationHistorySectionProps {
  sourceMetadata: ConversationSourceMetadata | null | undefined
}

/**
 * Affiche l'historique de conversation + la note du contributeur pour les
 * propositions de type EVENT_PAGE. Retourne null pour les autres sources,
 * ce qui permet de la monter inconditionnellement dans les vues simple et
 * groupée.
 */
export function ConversationHistorySection({ sourceMetadata }: ConversationHistorySectionProps) {
  if (!sourceMetadata || sourceMetadata.type !== 'EVENT_PAGE') return null

  const extra = sourceMetadata.extra ?? {}
  const history = (extra.conversationHistory as ChatTurn[] | undefined) ?? []
  const proposer = extra.proposer as ConversationProposer | undefined
  const note = extra.proposerNote as string | undefined

  if (history.length === 0 && !proposer && !note) return null

  const proposerLabel = proposer
    ? `${proposer.firstName ?? ''} ${proposer.lastName ?? ''}`.trim()
    : ''

  return (
    <Card sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Conversation
      </Typography>
      {proposer && (proposerLabel || proposer.email) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          {proposerLabel && <Chip label={proposerLabel} size="small" />}
          {proposer.email && (
            <Typography variant="caption" color="text.secondary">
              {proposer.email}
            </Typography>
          )}
        </Stack>
      )}
      <Stack spacing={1}>
        {history.map((turn, i) => {
          const { date, text, isBot } = parseTurn(turn)
          if (!date && !text) return null
          return (
            <Box
              key={i}
              sx={{
                p: 1.5,
                bgcolor: isBot ? 'action.selected' : 'action.hover',
                borderRadius: 1,
              }}
            >
              {(isBot || date) && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {isBot && <Chip label="Databot" size="small" />}
                  {date && (
                    <Typography variant="caption" color="text.secondary">
                      {date.toLocaleString('fr-FR')}
                    </Typography>
                  )}
                </Stack>
              )}
              {text && (
                <Typography
                  variant="body2"
                  sx={{ mt: isBot || date ? 0.5 : 0, whiteSpace: 'pre-wrap' }}
                >
                  {renderTextWithLinks(text)}
                </Typography>
              )}
            </Box>
          )
        })}
      </Stack>
      {note && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Note du contributeur
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {note}
          </Typography>
        </Box>
      )}
    </Card>
  )
}

interface ConversationHistoryListProps {
  /**
   * Toutes les propositions du groupe, dans l'ordre d'affichage voulu.
   * Le filtrage EVENT_PAGE est délégué à ConversationHistorySection, qui reste
   * la seule source de vérité sur « qu'est-ce qu'une source conversationnelle ».
   */
  proposals: Array<{ id: string; sourceMetadata?: ConversationSourceMetadata | null }>
}

/**
 * Variante groupée : rend la conversation de CHAQUE proposition du groupe qui
 * en porte une.
 *
 * Historiquement la vue groupée ne regardait que `groupProposals[0]`, or ce
 * tableau est trié par confiance décroissante — la proposition conversationnelle
 * n'y est donc pas forcément en tête, et la conversation disparaissait ou
 * semblait tirée au hasard parmi les propositions du groupe (MR-3368).
 */
export function ConversationHistoryList({ proposals }: ConversationHistoryListProps) {
  return (
    <>
      {proposals.map((proposal) => (
        <ConversationHistorySection key={proposal.id} sourceMetadata={proposal.sourceMetadata} />
      ))}
    </>
  )
}
